/**
 * Auto-login utility to check localStorage for valid authentication tokens
 * and automatically log in users if valid credentials are found
 */

/**
 * Checks if the user has valid authentication data in localStorage
 * @returns {boolean} - true if user has valid auth data, false otherwise
 */
export const hasValidAuthData = () => {
  try {
    const token = localStorage.getItem('ids_token');
    const userId = localStorage.getItem('ids_user_id');
    const userName = localStorage.getItem('ids_user_name');
    const orgId = localStorage.getItem('ids_org_id');
    
    // Check if all required authentication data exists
    return !!(token && userId && userName && orgId);
  } catch (error) {
    console.error('Error checking auth data:', error);
    return false;
  }
};

/**
 * Gets the stored authentication data from localStorage
 * @returns {object|null} - auth data object or null if not found
 */
export const getStoredAuthData = () => {
  try {
    if (!hasValidAuthData()) {
      return null;
    }
    
    return {
      token: localStorage.getItem('ids_token'),
      userId: localStorage.getItem('ids_user_id'),
      userName: localStorage.getItem('ids_user_name'),
      orgId: localStorage.getItem('ids_org_id'),
      orgName: localStorage.getItem('ids_org_name'),
      userMail: localStorage.getItem('ids_user_mail'),
      authLevel: localStorage.getItem('ids_auth_level'),
      isFreeTrial: localStorage.getItem('ids_is_free_trial'),
      firstTimeLogin: localStorage.getItem('ids_first_time_login')
    };
  } catch (error) {
    console.error('Error getting stored auth data:', error);
    return null;
  }
};

/**
 * Performs auto-login if valid authentication data exists
 * @param {function} navigate - React Router navigate function
 * @returns {boolean} - true if auto-login was successful, false otherwise
 */
export const performAutoLogin = (navigate) => {
  try {
    if (!hasValidAuthData()) {
      console.log('No valid auth data found for auto-login');
      return false;
    }
    
    const authData = getStoredAuthData();
    if (!authData) {
      console.log('Could not retrieve auth data for auto-login');
      return false;
    }
    
    console.log('Auto-login: Valid authentication data found, redirecting to Cloo AI');
    
    // Navigate to Cloo AI home (org AI agent home), the post-login landing page
    navigate('/v2/discovery/ai-agent-home');
    
    return true;
  } catch (error) {
    console.error('Error during auto-login:', error);
    return false;
  }
};

/**
 * Clears all authentication data from localStorage
 */
export const clearAuthData = () => {
  try {
    const authKeys = [
      'ids_token',
      'ids_user_id', 
      'ids_user_name',
      'ids_org_id',
      'ids_org_name',
      'ids_user_mail',
      'ids_auth_level',
      'ids_is_free_trial',
      'ids_first_time_login'
    ];
    
    authKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log('Authentication data cleared from localStorage');
  } catch (error) {
    console.error('Error clearing auth data:', error);
  }
};
